import React, { memo, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import { FaPlus, FaMinus } from "react-icons/fa";
import { MenuItem } from "../redux/slices/menuItemsSlice";
import { formatPrice } from "../utils/priceUtils";
import { getCartQuantity, getCartItemId } from "../utils/cartUtils";
import PrimeMenuItemCardMobile from "./PrimeMenuItemCardMobile";

interface PrimeMenuItemCardProps {
  item: MenuItem;
  onItemClick?: (item: MenuItem) => void;
  onAddToCart?: (item: MenuItem) => void;
  showAddToCart?: boolean;
  cartItems?: any[];
  onUpdateQuantity?: (cartItemId: string, quantity: number) => void;
  onIncrementQuantity?: (item: MenuItem) => void;
  onDecrementQuantity?: (itemId: string) => void;
}

// Subcomponent: Prime Menu Item Image
const PrimeMenuItemImage: React.FC<{
  item: MenuItem;
  onImageError: (e: React.SyntheticEvent<HTMLImageElement>) => void;
}> = ({ item, onImageError }) => (
  <div className="relative w-full h-full overflow-hidden">
    {item.image ? (
      <motion.img
        src={item.image}
        alt={`${item.name} - Premium menu item available for order`}
        className="w-full h-full object-cover"
        onError={onImageError}
        loading="lazy"
        whileHover={{ scale: 1.05 }}
        transition={{ duration: 0.6 }}
      />
    ) : (
      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
        <motion.span
          className="text-6xl md:text-8xl font-bold uppercase text-white font-serif drop-shadow-lg"
          whileHover={{ scale: 1.1, rotate: 5 }}
          transition={{ duration: 0.3 }}
        >
          {item.name.charAt(0)}
        </motion.span>
      </div>
    )}


    {/* Premium overlay gradient */}
    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-black/20" />
  </div>
);

// Subcomponent: Prime Menu Item Content
const PrimeMenuItemContent: React.FC<{ item: MenuItem }> = ({ item }) => (
  <motion.div 
    className="pt-2 md:pt-3 px-3 md:px-4 flex flex-col relative"
    initial={{ opacity: 0, x: 20 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ delay: 0.2, duration: 0.4 }}
  >
    {/* Top Section */}
    <div>
      {/* Title */}
      <div className="mb-2">
        <h3 className="text-base md:text-lg font-bold line-clamp-1 text-gray-900">
          {item.name}
        </h3>
      </div>

      {/* Description */}
      {item.description && (
        <p className="text-gray-600 text-xs md:text-sm leading-relaxed min-h-[2rem] md:min-h-[2.5rem] mb-3 md:mb-4 line-clamp-2">
          {item.description}
        </p>
      )}

    </div>
  </motion.div>
);

// Subcomponent: Prime Menu Item CTA
const PrimeMenuItemCTA: React.FC<{
  item: MenuItem;
  isOutOfStock: boolean;
  showAddToCart: boolean;
  cartQuantity: number;
  cartItemId: string;
  onAddToCart: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onUpdateQuantity?: (cartItemId: string, quantity: number) => void;
  onIncrementQuantity?: (item: MenuItem) => void;
  onDecrementQuantity?: (itemId: string) => void;
}> = ({ item, isOutOfStock, showAddToCart, cartQuantity, cartItemId, onAddToCart, onUpdateQuantity, onIncrementQuantity, onDecrementQuantity }) => {
  // Extract price from object or use as-is
  const displayPrice = typeof item.price === 'object' ? item.price.online : item.price;
  
  return (
    <div className={`px-3 md:px-4 ${!item.image ? 'pb-2 md:pb-3' : ''}`}>
      {/* Price and Button - Same Row */}
      <div className="flex items-center justify-between">
        {/* Price */}
        <span className="text-base md:text-lg font-bold text-gray-900">
          {formatPrice(displayPrice)}
        </span>

      {/* CTA */}
      {showAddToCart && (
        cartQuantity > 0 ? (
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (onDecrementQuantity) {
                  onDecrementQuantity(item.pk_id.toString());
                } else if (onUpdateQuantity) {
                  onUpdateQuantity(cartItemId, cartQuantity - 1);
                }
              }}
              className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center transition-all"
              aria-label="Decrease quantity"
            >
              <FaMinus className="w-3 h-3 text-gray-700" />
            </button>
            <span className="font-bold text-lg text-gray-900 min-w-[2rem] text-center">{cartQuantity}</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (onIncrementQuantity) {
                  onIncrementQuantity(item);
                } else if (onUpdateQuantity) {
                  onUpdateQuantity(cartItemId, cartQuantity + 1);
                }
              }}
              className="w-8 h-8 rounded-full text-white flex items-center justify-center transition-all"
              style={{ backgroundColor: '#EF4322' }}
              aria-label="Increase quantity"
            >
              <FaPlus className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <motion.button
            type="button"
            disabled={isOutOfStock}
            aria-disabled={isOutOfStock}
            className={`py-2 px-4 text-sm font-bold rounded-lg transition-all duration-300 font-serif uppercase tracking-wide outline-none focus:outline-none focus-visible:outline-none ${
              isOutOfStock
                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                : 'text-white focus:ring-0 focus-visible:ring-0 shadow-lg'
            }`}
            style={!isOutOfStock ? { backgroundColor: '#EF4322' } : undefined}
            onClick={onAddToCart}
            aria-label={`Add ${item.name} to cart${isOutOfStock ? ' - Out of Stock' : ''}`}
            data-testid={`add-to-cart-${item.id || item.pk_id}`}
            whileHover={!isOutOfStock ? { 
              scale: 1.05
            } : {}}
            whileTap={!isOutOfStock ? { scale: 0.95 } : {}}
          >
            {isOutOfStock ? 'Out of Stock' : 'Add'}
          </motion.button>
        )
      )}
    </div>
    </div>
  );
};

const PrimeMenuItemCard: React.FC<PrimeMenuItemCardProps> = ({
  item,
  onItemClick,
  onAddToCart,
  showAddToCart = true,
  cartItems = [],
  onUpdateQuantity,
  onIncrementQuantity,
  onDecrementQuantity,
}) => {
  // Early exit - only hide if disabled or not verified, but show out-of-stock items
  if (!item.is_enabled || !item.is_item_verified) {
    return null;
  }

  // Check if item is out of stock
  const isOutOfStock = !item.inventoryStatus;

  // Get cart quantity for this item
  const cartQuantity = useMemo(() => 
    getCartQuantity(item.pk_id.toString(), cartItems),
    [item.pk_id, cartItems]
  );

  const cartItemId = useMemo(() => 
    getCartItemId(item.pk_id.toString()),
    [item.pk_id]
  );

  const handleImageError = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      (e.target as HTMLImageElement).src = `https://via.placeholder.com/400x300/1a1a1a/d4af37?text=${encodeURIComponent(
        item.name
      )}`;
    },
    [item.name]
  );

  const handleCardClick = useCallback(() => {
    onItemClick?.(item);
  }, [onItemClick, item]);

  const handleAddToCart = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (!isOutOfStock) {
        onAddToCart?.(item);
      }
    },
    [onAddToCart, item, isOutOfStock]
  );

  // Extract price for aria-label
  const displayPrice = typeof item.price === 'object' ? item.price.online : item.price;

  return (
    <>
      {/* Mobile Version - Hidden on md: and above */}
      <div className="block md:hidden">
        <PrimeMenuItemCardMobile
          item={item}
          onItemClick={onItemClick}
          onAddToCart={onAddToCart}
          showAddToCart={showAddToCart}
          cartItems={cartItems}
          onUpdateQuantity={onUpdateQuantity}
          onIncrementQuantity={onIncrementQuantity}
          onDecrementQuantity={onDecrementQuantity}
        />
      </div>

      {/* Desktop Version - Hidden below md: */}
      <motion.div
        className="hidden md:block relative overflow-hidden cursor-pointer group border-2 border-gray-200 rounded-3xl bg-white outline-none focus:outline-none focus-visible:outline-none"
        style={{ outline: 'none' }}
        onClick={handleCardClick}
        role="button"
        tabIndex={0}
        aria-label={`View details for ${item.name}, priced at ${formatPrice(
          displayPrice
        )}${isOutOfStock ? ' - Out of Stock' : ''}`}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && handleCardClick()}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        data-testid={`menu-item-card-${item.id || item.pk_id}`}
      >

        {/* Consistent Grid Layout - All Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5">
          {/* Content Section - LEFT (3 columns = 60% with image, 100% without) */}
          <div className={`col-span-1 ${!item.image ? 'md:col-span-5' : 'md:col-span-3'} flex flex-col pb-2 md:pb-3`}>
            <PrimeMenuItemContent item={item} />
            <PrimeMenuItemCTA 
              item={item}
              isOutOfStock={isOutOfStock}
              showAddToCart={showAddToCart}
              cartQuantity={cartQuantity}
              cartItemId={cartItemId}
              onAddToCart={handleAddToCart}
              onUpdateQuantity={onUpdateQuantity}
              onIncrementQuantity={onIncrementQuantity}
              onDecrementQuantity={onDecrementQuantity}
            />
          </div>
          
          {/* Image Section - RIGHT (2 columns = 40%) */}
          {item.image && (
            <div className="col-span-1 md:col-span-2 h-full max-h-[140px] md:max-h-[160px]">
              <PrimeMenuItemImage 
                item={item} 
                onImageError={handleImageError} 
              />
            </div>
          )}
        </div>

      </motion.div>
    </>
  );
};

// Optimized memoization - only check critical props that affect rendering
const areEqual = (prev: PrimeMenuItemCardProps, next: PrimeMenuItemCardProps) => {
  return (
    // Core item identity and critical display props
    (prev.item.id || prev.item.pk_id) === (next.item.id || next.item.pk_id) &&
    prev.item.price === next.item.price &&
    prev.item.is_enabled === next.item.is_enabled &&
    prev.item.inventoryStatus === next.item.inventoryStatus &&
    prev.item.is_item_verified === next.item.is_item_verified &&
    // Component behavior props
    prev.showAddToCart === next.showAddToCart &&
    prev.onItemClick === next.onItemClick &&
    prev.onAddToCart === next.onAddToCart &&
    prev.cartItems === next.cartItems
  );
};

export default memo(PrimeMenuItemCard, areEqual);
